import { makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import type { StyleClasses } from '@paintscout/ui'
import { Grid, Typography, useIsMobile } from '@paintscout/ui'
import React from 'react'
import type { PresentationSectionProps } from '../../../..'

const useStyles = makeStyles<Theme, ExploreExterior>(
  (theme) => ({
    root: {
      background: theme.palette.common.white,
      boxShadow:
        '0px 1px 3px 0px rgba(0, 0, 0, 0.12), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 2px 1px -1px rgba(0, 0, 0, 0.20)',
      overflow: 'hidden'
    },
    exploreExteriorPaint: {
      position: 'relative'
    },
    exploreExteriorGridWrapper: {
      position: 'relative',
      zIndex: 1,
      textAlign: 'center'
    },
    exploreExteriorPaintTitle: {
      margin: '28px 0',
      fontFamily: 'CustomFont-SW',
      fontSize: '44px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '48px',
      letterSpacing: '-1.5px',
      [theme.breakpoints.down('md')]: {
        padding: '0 15px'
      }
    },
    exploreExteriorPaintLink: {
      color: '#000',
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline'
      }
    },
    exploreExteriorPaintLinkText: {
      textAlign: 'left',
      margin: '9px 0',
      fontFamily: 'CustomFont-SW',
      fontSize: '20px',
      fontStyle: 'normal',
      fontWeight: 500,
      lineHeight: '24px',
      letterSpacing: '-0.5px'
    },
    exploreExteriorPaintDescription: {
      marginBottom: '20px',
      textAlign: 'left',
      fontFamily: 'CustomFont-SW',
      fontSize: '19.5px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '24px',
      letterSpacing: '-0.5px'
    },
    exploreExteriorPaintBackground: {
      position: 'absolute',
      zIndex: 0,
      top: 0,
      left: 0,
      width: '100%',
      height: '350px',
      background: '#F6F0E8',
      [theme.breakpoints.down('sm')]: {
        display: 'none'
      }
    },
    contentGridContainer: {
      padding: '0 20px',
      [theme.breakpoints.down('md')]: {
        justifyContent: 'center'
      }
    },
    image: {
      width: '100%',
      [theme.breakpoints.down('sm')]: {
        maxWidth: '250px'
      },
      [theme.breakpoints.down(450)]: {
        maxWidth: '100%'
      }
    }
  }),
  { name: 'ExploreExterior' }
)

export interface ExploreExterior {
  classes?: StyleClasses<typeof useStyles>
}

function ExploreExterior(props: PresentationSectionProps) {
  const classes = useStyles(props)
  const isXs = useIsMobile({ custom: 700 })

  return (
    <div className={classes.root}>
      <div className={classes.exploreExteriorPaint}>
        <Grid container className={classes.exploreExteriorGridWrapper}>
          <Grid item xs={12}>
            <Typography variant="h2" className={classes.exploreExteriorPaintTitle}>
              Explore Exterior Paint Colors
            </Typography>
          </Grid>
          <Grid item justifyContent="center" spacing={2}>
            <Grid container>
              {exploreExteriorPaintItems.map((item) => (
                <Grid item container key={item.href} xs={isXs ? 12 : 4} sm={4} className={classes.contentGridContainer}>
                  <Grid item xs={12}>
                    <img src={item.image} className={classes.image} />
                  </Grid>
                  <Grid item xs={12}>
                    <a href={item.href} className={classes.exploreExteriorPaintLink} target="_blank" rel="noreferrer">
                      <Typography className={classes.exploreExteriorPaintLinkText}>{item.title}</Typography>
                    </a>
                  </Grid>
                  <Grid item>
                    <Typography variant="body1" className={classes.exploreExteriorPaintDescription}>
                      {item.description}
                    </Typography>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
        <div className={classes.exploreExteriorPaintBackground} />
      </div>
    </div>
  )
}

export default ExploreExterior

type ExploreExteriorPaintItem = {
  image: string
  title: React.ReactNode
  href: string
  description: React.ReactNode
}

const exploreExteriorPaintItems: ExploreExteriorPaintItem[] = [
  {
    image: 'https://paintscout.s3.us-east-1.amazonaws.com/partners/sw/images/explore-exterior-img-a.png',
    title: <>Top Exterior Colors &gt;</>,
    href: 'https://www.sherwin-williams.com/en-us/color/exterior-paint-colors/exterior-house-colors',
    description: (
      <>Find the ideal exterior paint colors from Sherwin-Williams to delight visitors and highlight your style.</>
    )
  },
  {
    image: 'https://paintscout.s3.us-east-1.amazonaws.com/partners/sw/images/explore-exterior-img-b.png',
    title: <>Front Door Colors &gt;</>,
    href: 'https://www.sherwin-williams.com/en-us/color/exterior-paint-colors/front-door-paint-colors',
    description: (
      <>
        By choosing a new front door paint color, you can quickly upgrade your exterior while infusing it with a splash
        of personal style.
      </>
    )
  },
  {
    image: 'https://paintscout.s3.us-east-1.amazonaws.com/partners/sw/images/explore-exterior-img-c.png',
    title: <>Garage Door Colors &gt;</>,
    href: 'https://www.sherwin-williams.com/en-us/color/exterior-paint-colors/garage-door-colors',
    description: (
      <>Stand out with a contrasting hue choice or harmonize by coordinating with your home's exterior colors.</>
    )
  }
]
